import {
  UseMutationResult,
  UseQueryResult,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import {
  ReportFiltersResponse,
  ReportPreviewResponse,
  ReportResponse,
  SecurityV2ReportPreviewResponse,
  UseReportsPreviewQueryOptions,
  UseReportsQueryOptions,
  SecurityReportKeyRecommendation,
  SecurityReportTabs,
  securityReportSeverityOrder,
} from './types';
import { API_ENDPOINTS } from '@api/ApiEndpoints';
import { useHttpClient } from '@hooks/utils/httpConfig';
import axios from 'axios';

export function useReportQuery({
  searchTerm,
  sortBy,
  order,
  status,
  pageSize = 20,
  pageNumber = 1,
  groupTypeList,
  startDate,
  endDate,
}: UseReportsQueryOptions = {}): UseQueryResult<ReportResponse> {
  const reportClient = useHttpClient();
  const fetchReports = async (): Promise<ReportResponse> => {
    const baseParams: Record<string, string> = {
      pageSize: pageSize.toString(),
      pageNumber: pageNumber.toString(),
    };

    if (searchTerm) baseParams.searchTerm = searchTerm;
    if (sortBy) baseParams.sortBy = sortBy;
    if (order) baseParams.order = order;
    if (status) baseParams.status = status;

    if (startDate) {
      baseParams.startDate = startDate;
    }

    if (endDate) {
      baseParams.endDate = endDate;
    }

    const reportTypeListParams = groupTypeList
      ? groupTypeList.map((type) => `ReportGroupTypeList=${type}`).join('&')
      : '';

    const endpoint = `${API_ENDPOINTS.REPORTS}?${new URLSearchParams(
      baseParams
    ).toString()}&${reportTypeListParams}`;

    return reportClient.get<ReportResponse>(endpoint);
  };

  return useQuery({
    queryKey: [
      'reports',
      pageSize,
      pageNumber,
      searchTerm,
      sortBy,
      order,
      status,
      groupTypeList,
      startDate,
      endDate,
    ],
    queryFn: fetchReports,
  });
}

export function useReportPreviewQuery({
  reportId,
}: UseReportsPreviewQueryOptions = {}): UseQueryResult<ReportPreviewResponse & { jsonReportData?: string }> {
  const reportClient = useHttpClient();
  const fetchReportPreview = async (): Promise<ReportPreviewResponse & { jsonReportData?: string }> => {

    const endpoint = `${API_ENDPOINTS.REPORTS}/${reportId}/details`;    
    const response = await reportClient.get<ReportPreviewResponse>(endpoint);
    
    // Fetch JSON data from SAS URL if available
    if (response.jsonReportSASUrl) {
      try {
        const jsonResponse = await axios.get(response.jsonReportSASUrl);
        return {
          ...response,
          jsonReportData: typeof jsonResponse.data === 'string' ? jsonResponse.data : JSON.stringify(jsonResponse.data)
        };
      } catch (error) {
        console.error('Error fetching JSON report data from SAS URL:', error);
        return response;
      }
    }
    
    return response;
  };

  return useQuery({
    queryKey: ['reportPreview', reportId],
    queryFn: fetchReportPreview,
    staleTime: 10 * 60 * 1000,
  });
}
export function useSecurityReportPreviewQuery<T>({
  reportId,
  selectedTab,
}: UseReportsPreviewQueryOptions & { selectedTab?: string }): UseQueryResult<SecurityV2ReportPreviewResponse<T>> {

  const reportClient = useHttpClient();
  const tab = SecurityReportTabs[selectedTab || ""] || selectedTab;

  // Reuse the same queryKey and queryFn as useReportPreviewQuery so the JSON blob
  // is downloaded exactly once and shared across the layout and all tab components.
  // Tab-specific filtering is done client-side in `select` — no extra network calls.
  const fetchReportPreview = async (): Promise<ReportPreviewResponse & { jsonReportData?: string }> => {
    const endpoint = `${API_ENDPOINTS.REPORTS}/${reportId}/details`;
    const response = await reportClient.get<ReportPreviewResponse>(endpoint);

    if (response.jsonReportSASUrl) {
      try {
        const jsonResponse = await axios.get(response.jsonReportSASUrl);
        return {
          ...response,
          jsonReportData: typeof jsonResponse.data === 'string' ? jsonResponse.data : JSON.stringify(jsonResponse.data),
        };
      } catch (error) {
        console.error('Error fetching JSON report data from SAS URL:', error);
        return response;
      }
    }

    return response;
  };

  return useQuery<ReportPreviewResponse & { jsonReportData?: string }, Error, SecurityV2ReportPreviewResponse<T>>({
    queryKey: ['reportPreview', reportId],
    queryFn: fetchReportPreview,
    staleTime: 10 * 60 * 1000,
    select: (data): SecurityV2ReportPreviewResponse<T> => {
      const empty: SecurityV2ReportPreviewResponse<T> = {
        currentPage: 1, itemCount: 0, items: [], pageSize: 0, totalItemCount: 0, totalPageCount: 1,
      };

      if (!data?.jsonReportData) return empty;

      const parsedJsonReportData = JSON.parse(data.jsonReportData);
      const components: any[] = parsedJsonReportData?.components?.filter(
        (component: any) => component?.keyParameters?.some((kp: any) => kp.key === tab)
      ) ?? [];

      let items: T[] = components.map((component: any) => ({
        type: component.type,
        parameters: component.keyParameters?.find((kp: any) => kp.key === tab)?.parameters,
      })) as unknown as T[];

      if (tab === SecurityReportTabs["key-recommendations"]) {
        const rows = components[0]?.keyParameters?.find((kp: any) => kp.key === tab)?.parameters?.Rows ?? [];
        items = rows.flat().map((row: any) => {
          const item: { [key: string]: string } = {};
          Object.keys(row).forEach(key => {
            const newKey = key.charAt(0).toLowerCase() + key.slice(1);
            item[newKey] = row[key];
          });
          return item;
        }).sort((a: SecurityReportKeyRecommendation, b: SecurityReportKeyRecommendation) =>
          securityReportSeverityOrder[a.recommendationSeverity] - securityReportSeverityOrder[b.recommendationSeverity]
        ) as unknown as T[];
      }

      return {
        currentPage: 1,
        itemCount: items.length,
        items,
        pageSize: items.length,
        totalItemCount: items.length,
        totalPageCount: 1,
      };
    },
  });
}

export const useReportPdfUrlPreviewQuery = (
  reportId: string | undefined
): UseMutationResult<unknown, unknown, void, unknown> => {
  const groupsClient = useHttpClient();
  const API_ENDPOINT = `${API_ENDPOINTS.REPORTS}/${reportId}/download-url`;

  return useMutation({
    mutationFn: () => groupsClient.get<any>(API_ENDPOINT),
  });
};

export function useReportFiltersQuery(): UseQueryResult<ReportFiltersResponse> {
  const reportClient = useHttpClient();
  const fetchReportFilters = async (): Promise<ReportFiltersResponse> => {
    const endpoint = `${API_ENDPOINTS.REPORTS_FILTER}`;

    return reportClient.get<ReportFiltersResponse>(endpoint);
  };

  return useQuery({
    queryKey: ['reportsFilters'],
    queryFn: fetchReportFilters,
  });
}
